/**
 * Quotes Comparison data service: async adapter over the Procurement/QuoteComparison API.
 * Returns the same object shapes the comparison screens already use (CSNumber, Vendors[], Items[], ...).
 * Matrix row key (item.id) = requisition line id (PR comparison) or material id (direct comparison).
 */
import {
  GetAllComparisonsApi,
  GetComparisonByIdApi,
  GetComparisonCandidatesApi,
  GetDirectCandidatesApi,
  CreateComparisonApi,
  EditComparisonApi,
  DeleteComparisonApi,
} from "../../../../../Api/Procurement/QuoteComparison";
import { unwrap, day, fileNameFromUrl } from "../Quotes/quotesService";

const NOT_SUPPLIED = "-- (Not Supplied / N/A)";
const r2 = (x) => Math.round((x + Number.EPSILON) * 100) / 100;
const rowKey = (x) => x.PurchaseRequisitionLineId || x.MaterialId;

const toItems = (rows) =>
  (rows || []).map((l, i) => {
    const qty = Number(l.Quantity ?? l.OpenQuantity) || 0;
    return {
      id: rowKey(l),
      prLineId: l.PurchaseRequisitionLineId || "",
      materialId: l.MaterialId,
      label: `Item ${i + 1}: ${l.Material} (${qty} ${l.Unit})`,
      materialName: l.Material,
      qty,
      uom: l.Unit,
    };
  });

// A backend quote -> one vendor slot of the matrix, with a rate cell per item.
export const slotFromQuote = (q, items, slot, awarded = false) => {
  const byKey = {};
  (q.Rates || []).forEach((r) => {
    byKey[rowKey(r)] = r;
  });
  const Rates = {};
  items.forEach((it) => {
    const r = byKey[it.id];
    const supplied = Boolean(r) && r.Rate != null && r.QuoteLineId != null;
    const qty = Number(r?.Quantity ?? it.qty) || 0;
    Rates[it.id] = {
      supplied,
      unitRate: supplied ? Number(r.Rate) : 0,
      total: supplied ? Number(r.Rate) * qty : 0,
      note: supplied ? "" : NOT_SUPPLIED,
      quoteLineId: supplied ? r.QuoteLineId : null,
      qty,
      // direct quotes only: what can still be awarded on this quote line (null for PR rows)
      remaining: r?.RemainingQuantity ?? null,
    };
  });
  return {
    slot,
    QuoteId: q.QuoteId,
    QuoteNo: q.QuoteNumber,
    VendorName: q.VendorName,
    AttachmentName: q.FileName || fileNameFromUrl(q.File),
    AttachmentUrl: q.File || "",
    CarriageFreight: Number(q.FreightAmount) || 0,
    CarriageNote: "",
    PaymentTerms: q.PaymentTerms || "",
    DeliveryLeadTime: q.DeliveryLeadTime || "",
    Expired: Boolean(q.Expired),
    Awarded: awarded,
    Rates,
  };
};

const requisitionLabel = (c, items) =>
  c.PrNumber
    ? `${c.PrNumber} (${items.map((i) => i.materialName).join(" & ")})`
    : "Direct (no requisition)";

export const getComparisons = async () => {
  const rows = unwrap(await GetAllComparisonsApi()) || [];
  return rows.map((c) => ({
    Id: c.Id,
    CSNumber: c.ComparisonNumber,
    ProjectId: c.ProjectId,
    ProjectName: c.ProjectName,
    RequisitionId: c.PurchaseRequisitionId || "",
    RequisitionNo: c.PrNumber || "",
    RequisitionLabel: c.PrNumber || "Direct (no requisition)",
    IsDirect: Boolean(c.IsDirect),
    CSDate: day(c.ComparisonDate || c.CreatedAt),
    Status: c.Status,
    Remarks: c.Narration || "",
    AwardedVendors: c.AwardedVendors || [],
    Vendors: (c.Vendors || []).map((n) => ({
      VendorName: n,
      Awarded: (c.AwardedVendors || []).includes(n),
    })),
    HasDecision: Boolean(c.HasDecision),
    IsBlocked: Boolean(c.IsBlocked),
    BlockedReason: c.BlockedReason || "",
  }));
};

export const getComparisonById = async (id) => {
  const c = unwrap(await GetComparisonByIdApi(id));
  const items = toItems(c.Items);
  const winners = new Set((c.Lines || []).map((l) => l.WinnerQuoteId));
  return {
    Id: c.Id,
    CSNumber: c.ComparisonNumber,
    ProjectId: c.ProjectId,
    ProjectName: c.ProjectName,
    RequisitionId: c.PurchaseRequisitionId || "",
    RequisitionNo: c.PrNumber || "",
    RequisitionLabel: requisitionLabel(c, items),
    IsDirect: Boolean(c.IsDirect),
    CSDate: day(c.ComparisonDate || c.CreatedAt),
    Status: c.Status,
    Remarks: c.Narration || "",
    Items: items,
    Vendors: (c.Quotes || []).map((q, i) => slotFromQuote(q, items, i + 1, winners.has(q.QuoteId))),
    AwardedVendors: [...new Set((c.Lines || []).map((l) => l.WinnerVendorName).filter(Boolean))],
    Lines: c.Lines || [],
    IsBlocked: Boolean(c.IsBlocked),
    BlockedReason: c.BlockedReason || "",
    Approvals: c.Approvals || [],
  };
};

// Matrix rows and quotes to pick from: a requisition's quotes, or (no requisition) the project's direct quotes.
export const getCandidates = async (requisitionId, projectId) => {
  const res = requisitionId
    ? await GetComparisonCandidatesApi(requisitionId)
    : await GetDirectCandidatesApi(projectId);
  const d = unwrap(res);
  // Quotes stay in the backend shape; the screen turns a picked one into a slot with slotFromQuote.
  return { Items: toItems(d.Lines), Quotes: d.Quotes || [] };
};

/**
 * Award rule: per item the cheapest TICKED vendor wins the quantity (PR: the open quantity; direct: the
 * quoted quantity, at most what is still left on that quote line); when that is not the cheapest
 * among all picked quotes the Evaluation Remarks become the line Reason (and are then required).
 * Edit: savedLines (the comparison as stored) keep their winner and quantity unless the ticks that
 * matter for that item changed.
 */
export const buildAwardLines = (items, vendors, remarks, savedLines = []) => {
  const picked = vendors.filter((v) => v.QuoteId);
  const ticked = picked.filter((v) => v.Awarded);
  if (ticked.length === 0) {
    throw new Error("Please tick Award for at least one vendor");
  }
  const reason = (remarks || "").trim();
  const rateOf = (v, it) => (v.Rates?.[it.id]?.supplied ? Number(v.Rates[it.id].unitRate) : null);
  const savedByKey = {};
  savedLines.forEach((l) => {
    savedByKey[rowKey(l)] = l;
  });
  const savedTicked = new Set(savedLines.map((l) => l.WinnerQuoteId));
  const lines = [];
  items.forEach((it) => {
    const candidates = ticked.filter((v) => rateOf(v, it) !== null);
    // the item's ticks are unchanged when the same quotes that supplied it are ticked as when saved
    const suppliers = picked.filter((v) => rateOf(v, it) !== null);
    const unchanged = suppliers.every((v) => v.Awarded === savedTicked.has(v.QuoteId));
    const saved = savedByKey[it.id];
    const kept =
      saved && unchanged
        ? candidates.find((v) => v.Rates[it.id].quoteLineId === saved.WinnerQuoteLineId)
        : null;
    if (candidates.length === 0) {
      return;
    }
    // reduce keeps the earlier slot on a tie
    const win = kept || candidates.reduce((b, v) => (rateOf(v, it) < rateOf(b, it) ? v : b));
    const lowest = Math.min(...picked.map((v) => rateOf(v, it)).filter((r) => r !== null));
    const notLowest = rateOf(win, it) > lowest;
    if (notLowest && !reason) {
      throw new Error(
        `Please enter Evaluation Remarks: the awarded vendor for ${it.materialName} is not the lowest rate`,
      );
    }
    const cell = win.Rates[it.id];
    let qty = it.prLineId ? it.qty : cell.qty;
    if (kept) {
      qty = Number(saved.Quantity);
    } else if (!it.prLineId && cell.remaining != null) {
      qty = Math.min(qty, Number(cell.remaining));
    }
    // direct comparison: the user may award less now and the rest on a later comparison
    if (!it.prLineId && cell.awardQty !== undefined && cell.awardQty !== "") {
      qty = Number(cell.awardQty);
      if (cell.remaining != null && qty > Number(cell.remaining)) {
        throw new Error(
          `Award quantity for ${it.materialName} cannot exceed the remaining ${cell.remaining}`,
        );
      }
    }
    if (!(qty > 0)) {
      throw new Error(`No remaining quantity to award for ${it.materialName}`);
    }
    lines.push({
      ...(it.prLineId ? { PurchaseRequisitionLineId: it.prLineId } : { MaterialId: it.materialId }),
      WinnerQuoteLineId: cell.quoteLineId,
      Quantity: r2(qty),
      Reason: notLowest ? reason : undefined,
    });
  });
  if (lines.length === 0) {
    throw new Error("The awarded vendors did not quote any of the items");
  }
  return lines;
};

// c: {Id (edit only), SavedLines (edit: Lines of GetById), RequisitionId ("" = direct), ProjectId, CSDate, Remarks, Vendors, Items, ApproverUserIds}
export const saveComparison = async (c) => {
  const picked = c.Vendors.filter((v) => v.QuoteId);
  if (picked.length === 0) {
    throw new Error("Please select at least one quote");
  }
  if (!c.ApproverUserIds?.length) {
    throw new Error("Please select at least one approver");
  }
  const body = {
    ProjectId: Number(c.ProjectId),
    ComparisonDate: c.CSDate,
    Narration: c.Remarks,
    QuoteIds: [...new Set(picked.map((v) => v.QuoteId))],
    ApproverUserIds: c.ApproverUserIds,
    Lines: buildAwardLines(c.Items, c.Vendors, c.Remarks, c.SavedLines),
  };
  if (c.RequisitionId) {
    body.PurchaseRequisitionId = c.RequisitionId;
  }
  const res = c.Id ? await EditComparisonApi(c.Id, body) : await CreateComparisonApi(body);
  return unwrap(res) || { Id: c.Id };
};

export const deleteComparison = async (id) => {
  unwrap(await DeleteComparisonApi(id));
  return getComparisons();
};
